import React, { useEffect, useMemo } from 'react';
import classNames from 'classnames';
import moment from 'moment';
import { useDispatch, useSelector } from 'react-redux';
import { useForm } from 'react-hook-form';
import {
  Dialog, DialogContent, DialogFooter, DialogTitle,
} from '../../../components/base/Dialog/Dialog';
import { responseLogWithNotification } from '../../../utils/utils';
import {
  Button, Col, Container, Icons, Row,
} from '../../../components/base/BootstrapComponents';
import {
  createInterval,
  editInterval,
  loadSubscriptionTypeList,
} from '../actions';
import notificationDecorator from '../../Notifications/notificationDecorator';
import { getSubscriptionData } from '../reducer';
import FieldDTO from '../../../components/fields/Abstract/FieldDTO';
import TextInputWithLabel from '../../../components/fields/TextInput/TextInputWithLabel';
import IntervalDTO from '../../../dto/IntervalDTO';
import DateTimePicker from '../../../components/fields/DatePicker/DateTimePicker';
import SelectInput from '../../../components/fields/SelectInput/SelectInput';

const formatPresetDuration = duration => {
  const totalSeconds = Number(duration);
  if (!Number.isFinite(totalSeconds) || totalSeconds < 0) {
    return '—';
  }

  if (totalSeconds % 86400 === 0) {
    const days = totalSeconds / 86400;
    return `${days} д.`;
  }

  if (totalSeconds % 3600 === 0) {
    const hours = totalSeconds / 3600;
    return `${hours} ч.`;
  }

  if (totalSeconds % 60 === 0) {
    const minutes = totalSeconds / 60;
    return `${minutes} мин.`;
  }

  return `${totalSeconds} сек.`;
};

export default function IntervalDialog(props) {
  const { handleClose = () => {}, loadData = () => {} } = props;
  const {
    currentInterval,
    currentSubscription,
    subscriptionTypeList,
    loadingSubscriptionTypeList,
  } = useSelector(getSubscriptionData);
  const dispatch = useDispatch();
  const {
    control,
    getValues,
    watch,
    setValue,
  } = useForm({
    defaultValues: {
      preset_id: '',
      ...(currentInterval || {}),
    },
  });

  const presetField = {
    name: 'preset_id',
    type: String,
    validators: [],
    labelKey: 'subs.preset.select',
  };

  const selectedPresetId = watch('preset_id');
  const startAt = watch('start_at');

  const presetOptions = useMemo(
    () => (subscriptionTypeList || []).map(item => ({
      value: item.id,
      name: item.name
        ? `${item.name} (${formatPresetDuration(item.duration)})`
        : formatPresetDuration(item.duration),
    })),
    [subscriptionTypeList],
  );

  const selectedPreset = useMemo(
    () => (subscriptionTypeList || []).find(item => item.id === selectedPresetId),
    [subscriptionTypeList, selectedPresetId],
  );

  useEffect(() => {
    dispatch(loadSubscriptionTypeList({ page: 1, limit: 1000 })).catch(() => {});
  }, [dispatch]);

  useEffect(() => {
    if (!selectedPreset || !startAt) {
      return;
    }

    const durationSeconds = Number(selectedPreset.duration);
    if (!Number.isFinite(durationSeconds) || durationSeconds <= 0) {
      return;
    }

    const nextEndAt = moment(startAt).add(durationSeconds, 'seconds').toISOString();
    setValue('end_at', nextEndAt, { shouldDirty: true, shouldValidate: false });
  }, [selectedPreset, setValue, startAt]);

  const handleSave = () => {
    const isUpdate = currentInterval?.id;
    const action = isUpdate ? editInterval : createInterval;
    const data = { ...getValues() };
    delete data.preset_id;

    dispatch(action({ data, id: currentSubscription.id, iId: currentInterval?.id }))
      .then(() => {
        handleClose();
        loadData();
        notificationDecorator({
          title: isUpdate ? 'Редактирование абонемента' : 'Создание абонемента',
          message: isUpdate ? 'Редактирование абонемента прошло успешно' : 'Создание абонемента прошло успешно',
        });
      })
      .catch(responseLogWithNotification);
  };

  const DTO = new IntervalDTO();
  const attributes = DTO.getAttributesByName();
  return (
    <Dialog
      className={classNames('interval-popup')}
      onClose={handleClose}
      // size="lg"
      open
    >
      <DialogTitle
        className="hff-title"
        title={(
          <>
            <div className="hff-title-text f2">Интервал</div>
            <div className="hff-close-wrapper">
              <Icons width={14} height={14} customIcon icon="clear" className="hff-close" onClick={handleClose} />
            </div>
          </>
        )}
      />
      <DialogContent className="hff-content">
        <Container>
          <Row>
            <Col>
              <FieldDTO
                control={control}
                type="text"
                className="mb-2"
                dtoObject={presetField}
                addPlaceholder
                component={SelectInput}
                withLabel
                options={presetOptions}
                disabled={loadingSubscriptionTypeList}
              />
              {selectedPreset ? (
                <div className="mb-3 text-muted small">
                  <div><strong>Пресет:</strong> {selectedPreset.name || 'Без названия'}</div>
                  <div><strong>Длительность:</strong> {formatPresetDuration(selectedPreset.duration)}</div>
                  <div><strong>Стоимость:</strong> {selectedPreset.price ?? '—'}</div>
                </div>
              ) : null}
            </Col>
          </Row>
          <Row>
            <Col>
              <FieldDTO
                control={control}
                type="text"
                className="mb-2"
                dtoObject={attributes.start_at}
                addPlaceholder
                component={DateTimePicker}
                withLabel
                onChangeToIso
              />
            </Col>
          </Row>
          <Row>
            <Col>
              <FieldDTO
                control={control}
                type="text"
                className="mb-2"
                dtoObject={attributes.end_at}
                addPlaceholder
                component={DateTimePicker}
                withLabel
                onChangeToIso
              />
            </Col>
          </Row>
          <Row>
            <Col>
              <FieldDTO
                control={control}
                type="text"
                className="mb-2"
                dtoObject={attributes.payment_id}
                addPlaceholder
                component={TextInputWithLabel}
              />
            </Col>
          </Row>
        </Container>
      </DialogContent>
      <DialogFooter className="hff-footer">
        <Button onClick={handleSave}>Сохранить</Button>
        <Button onClick={handleClose}>Закрыть</Button>
      </DialogFooter>
    </Dialog>
  );
}
